import * as React from "react";
import { useEffect, useState } from "react";
import {
  Calendar,
  CandlestickChart,
  Code2,
  FileChartLine,
  FileCode2,
  Layers,
  Loader2,
  Play,
  Plus,
  type LucideIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  METRIC_TOOLTIP_BODY_CLASS,
  METRIC_TOOLTIP_TITLE_CLASS,
  metricTooltipShellClassName,
} from "@/components/ui/metric-tooltip";
import { cn } from "@/lib/utils";
import { useIsBelowLg } from "@/hooks/use-mobile";
import {
  REPORTS_SYMBOL_BADGE_CLASS,
  ReportsMetaIconChip,
  getReportTypeBadgeClass,
  getReportTypeBadgeDataType,
} from "../Reports/components/ReportsCardChrome";
import { statisticsSidebarSurfaceCardClassName } from "../Statistics/components/statisticsShadcnCardShell";
import {
  formatSignedNumber,
} from "../ExecutionRulesSimulator/metricFormatters";
import {
  cnSimulatorMetricsDelta,
  SIMULATOR_METRICS_GRAY,
} from "../ExecutionRulesSimulator/simulatorMetricsTypography";
import {
  buildSimulationEntityCardDetail,
  simulationSummaryDeltas,
} from "./workspaceEntityCardDetails";
import type { Backtest } from "../../../types/backtests";
import type { Report, ReportStrategyResponse } from "../../../types/reports";
import {
  extractReportCohortSummary,
  getReportCandleTimeframeForDisplay,
  getReportCohortWindowLabel,
} from "../../../utils/reportListDisplay";
import { formatReportCardCurrency } from "../../../utils/reportListCardPreview";
import {
  masterPineUploadStatusLabel,
  strategyHasMasterPinescriptUploaded,
  strategyHasTradingViewPinescriptCreated,
  tradingViewPineCreatedStatusLabel,
} from "../../../utils/reportStrategyPineUi";
import { getReportTypeDisplayLabel } from "../../../utils/reportTypeRules";
import { getReportDisplayTitle } from "../../../utils/reportWindowLabel";
import {
  formatWorkspaceHubDate,
  WORKSPACE_HUB_ENTITY_TITLE_CLASS,
} from "./WorkspaceHubLinkCell";
import { Spinner } from "@/components/ui/spinner";

type HubEntityLaneKey = "strategies" | "reports" | "simulations";

type HubEntityLaneConfig = {
  key: HubEntityLaneKey;
  label: string;
  emptyLabel: string;
  createLabel: string;
  /** Colored icon box border. */
  iconBoxClass: string;
  /** Icon / mobile tab label color. */
  iconTextClass: string;
  Icon: typeof Layers;
};

const HUB_ENTITY_LANES: HubEntityLaneConfig[] = [
  {
    key: "strategies",
    label: "Strategies",
    emptyLabel: "No strategies for this account. Use + to create one or add an existing strategy.",
    createLabel: "Add strategy",
    iconBoxClass: "border-solid border-[#805AD5]/55",
    iconTextClass: "text-[#805AD5]",
    Icon: Layers,
  },
  {
    key: "reports",
    label: "Reports",
    emptyLabel: "No reports for this account.",
    createLabel: "Create report",
    iconBoxClass: "border-solid border-[#38BBD1]/55",
    iconTextClass: "text-[#38BBD1]",
    Icon: FileChartLine,
  },
  {
    key: "simulations",
    label: "Simulations",
    emptyLabel: "No simulations for this account.",
    createLabel: "Create simulation",
    iconBoxClass: "border-solid border-[color:var(--tessera-pnl-positive)]/55",
    iconTextClass: "text-[color:var(--tessera-pnl-positive)]",
    Icon: Play,
  },
];

const LANE_SHELL_CLASS = cn(
  "flex min-h-0 min-w-0 flex-1 flex-col rounded-[6px] border border-solid border-border bg-muted/40 shadow-none dark:bg-sidebar/40"
);

const LANE_COUNT_BADGE_CLASS =
  "h-5 min-w-5 justify-center rounded-full border border-solid border-border bg-muted/70 px-1.5 font-[family-name:var(--font-primary)] font-normal tabular-nums text-muted-foreground dark:bg-muted/40";

const ENTITY_CARD_CLASS = cn(
  "flex w-full min-w-0 flex-col gap-2.5 rounded-[6px] border border-solid border-border bg-card p-3 text-left text-card-foreground shadow-none ring-0 transition-colors",
  "hover:border-[#38BBD1]/50",
  statisticsSidebarSurfaceCardClassName
);

const CARD_TITLE_CLASS = cn(
  WORKSPACE_HUB_ENTITY_TITLE_CLASS,
  "line-clamp-2 w-full whitespace-normal border-0 bg-transparent p-0 text-left hover:underline"
);

const META_LABEL_CLASS =
  "shrink-0 font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-sidebar-foreground";

const CARD_DESCRIPTION_CLASS =
  "line-clamp-2 min-h-8 font-[family-name:var(--font-primary)] text-xs leading-snug text-gray-500 dark:text-sidebar-foreground";

const CARD_UPDATED_CLASS =
  "font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-sidebar-foreground";

const ENTITY_CARD_FOOTER_CLASS =
  "flex items-end justify-between gap-2 border-t border-border pt-2.5";

const ENTITY_CARD_OPEN_BUTTON_CLASS = cn(
  "h-7 min-h-7 w-auto shrink-0 px-1.5 font-[family-name:var(--font-primary)] text-xs font-normal",
  "normal-case tracking-normal [&_span]:normal-case [&_span]:tracking-normal [&_span]:text-xs [&_span]:font-normal"
);

const PINE_CHIP_BASE_CLASS =
  "aspect-square size-7 min-h-7 min-w-7 shrink-0 rounded-[6px] bg-transparent p-0 shadow-none";

const LINK_CHIP_BASE_CLASS = PINE_CHIP_BASE_CLASS;

/** Simulation origin badges — gray outline (distinct from Open gradient button). */
const ORIGIN_BADGE_CLASS = REPORTS_SYMBOL_BADGE_CLASS;

function HubEntityOpenButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}): React.ReactElement {
  return (
    <Button
      type="button"
      variant="gradient"
      size="xs"
      data-workspace-hub-entity-open=""
      className={ENTITY_CARD_OPEN_BUTTON_CLASS}
      style={{
        fontFamily: "var(--font-primary)",
        fontSize: "0.75rem",
        fontWeight: 400,
        textTransform: "none",
        letterSpacing: "normal",
        paddingInline: "0.375rem",
        height: "1.75rem",
        width: "auto",
      }}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <span>Open</span>
    </Button>
  );
}

function usePrefersHover(): boolean {
  const [prefersHover, setPrefersHover] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setPrefersHover(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return prefersHover;
}

/** Icon jump to a linked strategy / report — only rendered when that link exists. */
function HubEntityLinkIconButton({
  kind,
  detail,
  onOpen,
}: {
  kind: "strategy" | "report";
  detail?: string | null;
  onOpen: () => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const prefersHover = usePrefersHover();
  const label = kind === "strategy" ? "Strategy" : "Report";
  const Icon = kind === "strategy" ? Layers : FileChartLine;
  const detailText = detail?.trim() || `Open linked ${label.toLowerCase()}`;

  const reveal = (next: boolean) => {
    setOpen(next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!prefersHover) reveal(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon-xs"
          data-font="primary"
          data-workspace-hub-link-chip={kind}
          className={cn(
            LINK_CHIP_BASE_CLASS,
            kind === "strategy"
              ? "border-[#805AD5]/55 text-[#805AD5] hover:bg-[#805AD5]/10 hover:text-[#805AD5]"
              : "border-[#38BBD1]/55 text-[#38BBD1] hover:bg-[#38BBD1]/10 hover:text-[#38BBD1]"
          )}
          aria-label={`Open ${label}${detail?.trim() ? `: ${detail.trim()}` : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
          onPointerEnter={() => {
            if (prefersHover) reveal(true);
          }}
          onPointerLeave={() => {
            if (prefersHover) reveal(false);
          }}
        >
          <Icon className="size-3.5 shrink-0 text-current" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={6}
        className={cn(
          metricTooltipShellClassName("w-auto max-w-[min(100vw-2rem,16rem)]"),
          "gap-0 p-2.5 shadow-[0_0_12px_rgba(15,23,42,0.12),0_0_2px_rgba(15,23,42,0.08)] dark:shadow-[0_0_14px_rgba(0,0,0,0.55),0_0_2px_rgba(0,0,0,0.35)]"
        )}
        onPointerEnter={() => {
          if (prefersHover) reveal(true);
        }}
        onPointerLeave={() => {
          if (prefersHover) reveal(false);
        }}
      >
        <p className={METRIC_TOOLTIP_TITLE_CLASS}>{label}</p>
        <p className={cn("mt-1 break-words text-[11px] leading-snug", METRIC_TOOLTIP_BODY_CLASS)}>
          {detailText}
        </p>
      </PopoverContent>
    </Popover>
  );
}

/** Hover (desktop) / tap (mobile) — grey when missing, green when present. */
function StrategyPinePresenceChip({
  id,
  label,
  present,
  value,
  icon: Icon,
}: {
  id: string;
  label: string;
  present: boolean;
  value: string;
  icon: LucideIcon;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const prefersHover = usePrefersHover();

  const reveal = (next: boolean) => {
    setOpen(next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!prefersHover) reveal(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={present ? "outline-green" : "outline-gray"}
          size="icon-xs"
          data-font="primary"
          data-workspace-hub-pine-chip={id}
          data-workspace-hub-pine-present={present ? "true" : "false"}
          className={PINE_CHIP_BASE_CLASS}
          aria-label={`${label}: ${value}`}
          onClick={(event) => event.stopPropagation()}
          onPointerEnter={() => {
            if (prefersHover) reveal(true);
          }}
          onPointerLeave={() => {
            if (prefersHover) reveal(false);
          }}
        >
          <Icon className="size-3.5 shrink-0" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={6}
        className={cn(
          metricTooltipShellClassName("w-auto max-w-[min(100vw-2rem,16rem)]"),
          "gap-0 p-2.5 shadow-[0_0_12px_rgba(15,23,42,0.12),0_0_2px_rgba(15,23,42,0.08)] dark:shadow-[0_0_14px_rgba(0,0,0,0.55),0_0_2px_rgba(0,0,0,0.35)]"
        )}
        onPointerEnter={() => {
          if (prefersHover) reveal(true);
        }}
        onPointerLeave={() => {
          if (prefersHover) reveal(false);
        }}
      >
        <p className={METRIC_TOOLTIP_TITLE_CLASS}>{label}</p>
        <p className={cn("mt-1 break-words text-[11px] leading-snug", METRIC_TOOLTIP_BODY_CLASS)}>
          {value}
        </p>
      </PopoverContent>
    </Popover>
  );
}

export type WorkspaceHubEntitiesBoardProps = {
  strategies: ReportStrategyResponse[];
  reports: Report[];
  backtests: Backtest[];
  /** True while hub entity lists are fetching — show lane loading, not empty. */
  loading?: boolean;
  /** Report card ids currently fading out after a failed generate. */
  dismissingReportIds?: ReadonlySet<string>;
  onOpenStrategy: (strategy: ReportStrategyResponse) => void;
  onOpenReport: (report: Report) => void;
  onOpenSimulation: (backtest: Backtest) => void;
  /** Jump from a linked id on report/simulation cards. */
  onOpenStrategyById: (strategyId: string) => void;
  onOpenReportById: (reportId: string) => void;
  onCreateStrategy: () => void;
  onCreateReport: () => void;
  onCreateSimulation: () => void;
};

function laneCreateHandler(
  lane: HubEntityLaneConfig,
  props: Pick<
    WorkspaceHubEntitiesBoardProps,
    "onCreateStrategy" | "onCreateReport" | "onCreateSimulation"
  >
): () => void {
  if (lane.key === "strategies") return props.onCreateStrategy;
  if (lane.key === "reports") return props.onCreateReport;
  return props.onCreateSimulation;
}

function HubEntityLaneShell({
  lane,
  count,
  countLoading = false,
  onCreate,
  children,
  className,
}: {
  lane: HubEntityLaneConfig;
  count: number;
  countLoading?: boolean;
  onCreate: () => void;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  const Icon = lane.Icon;
  return (
    <section
      data-workspace-hub-entity-lane=""
      data-workspace-hub-entity-lane-key={lane.key}
      className={cn(LANE_SHELL_CLASS, className)}
    >
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span
            data-workspace-hub-lane-icon={lane.key}
            className={cn(
              "flex size-7 shrink-0 items-center justify-center rounded-[6px] border border-solid bg-card dark:bg-sidebar",
              lane.iconBoxClass
            )}
          >
            <Icon className={cn("size-3.5", lane.iconTextClass)} aria-hidden />
          </span>
          <h3
            data-workspace-hub-lane-title={lane.key}
            className={cn(
              "truncate font-[family-name:var(--font-primary)] text-sm font-semibold",
              lane.iconTextClass
            )}
          >
            {lane.label}
          </h3>
          <Badge
            variant="outline"
            data-workspace-hub-lane-count=""
            className={LANE_COUNT_BADGE_CLASS}
          >
            {countLoading ? "…" : count}
          </Badge>
        </div>
        <Button
          type="button"
          variant="gradient"
          size="icon"
          data-font="primary"
          data-workspace-hub-lane-create={lane.key}
          className="size-7 shrink-0"
          aria-label={lane.createLabel}
          onClick={onCreate}
        >
          <Plus className="size-3.5" aria-hidden />
        </Button>
      </header>
      <div
        data-workspace-hub-entity-lane-body=""
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3"
        style={{ maxHeight: "min(70vh, 720px)" }}
      >
        {children}
      </div>
    </section>
  );
}

function HubLaneEmpty({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex flex-1 flex-col items-center justify-center rounded-[6px] border border-dashed border-border bg-card/60 px-4 py-8 text-center dark:bg-sidebar/30">
      <p className={cn(META_LABEL_CLASS, "text-sm")}>{message}</p>
    </div>
  );
}

function HubLaneLoading({ label }: { label: string }): React.ReactElement {
  return (
    <div
      data-workspace-hub-lane-loading=""
      className="flex flex-1 flex-col items-center justify-center gap-3 rounded-[6px] border border-dashed border-border bg-card/60 px-4 py-10 text-center dark:bg-sidebar/30"
    >
      <Spinner
        className="size-4 text-[color:var(--brand-tropical-surf)]"
        aria-hidden
      />
      <div className="min-w-0">
        <p className="font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]">
          Loading {label.toLowerCase()}…
        </p>
        <p className={cn(META_LABEL_CLASS, "mt-1 text-sm")}>
          Fetching {label.toLowerCase()} for this account.
        </p>
      </div>
    </div>
  );
}

function StrategyEntityCard({
  strategy,
  onOpen,
}: {
  strategy: ReportStrategyResponse;
  onOpen: (strategy: ReportStrategyResponse) => void;
}): React.ReactElement {
  const name = strategy.name?.trim() || "Untitled strategy";
  const description = strategy.description?.trim() || "";
  const hasMaster = strategyHasMasterPinescriptUploaded(strategy);
  const hasActive = strategyHasTradingViewPinescriptCreated(strategy);

  return (
    <article
      data-workspace-hub-entity-card="strategies"
      className={ENTITY_CARD_CLASS}
    >
      <button
        type="button"
        data-workspace-hub-entity-title=""
        className={CARD_TITLE_CLASS}
        onClick={() => onOpen(strategy)}
      >
        {name}
      </button>

      <p className={CARD_DESCRIPTION_CLASS}>{description || "—"}</p>

      <div className={ENTITY_CARD_FOOTER_CLASS}>
        <div className="min-w-0">
          <p className={META_LABEL_CLASS}>Updated</p>
          <p className={CARD_UPDATED_CLASS}>{formatWorkspaceHubDate(strategy.updated_at)}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <div
            className="flex shrink-0 items-center gap-1.5"
            onClick={(event) => event.stopPropagation()}
          >
            <StrategyPinePresenceChip
              id="master-pine"
              label="Master Pinescript"
              present={hasMaster}
              value={masterPineUploadStatusLabel(strategy)}
              icon={FileCode2}
            />
            <StrategyPinePresenceChip
              id="active-pine"
              label="Active Pinescript"
              present={hasActive}
              value={tradingViewPineCreatedStatusLabel(strategy)}
              icon={Code2}
            />
          </div>
          <HubEntityOpenButton
            label={`Open ${name}`}
            onClick={() => onOpen(strategy)}
          />
        </div>
      </div>
    </article>
  );
}

/** Optimistic hub card before generate API returns a real report id. */
function isHubReportPendingId(id: string): boolean {
  return id.startsWith("pending-");
}

function ReportEntityCard({
  report,
  dismissing = false,
  onOpen,
  onOpenStrategyById,
}: {
  report: Report;
  dismissing?: boolean;
  onOpen: (report: Report) => void;
  onOpenStrategyById: (strategyId: string) => void;
}): React.ReactElement {
  const title = getReportDisplayTitle(report);
  const reportType = getReportTypeDisplayLabel(report.report_type);
  const windowLabel = getReportCohortWindowLabel(report);
  const timeframe = getReportCandleTimeframeForDisplay(report)?.trim() || "—";
  const rangeStats = extractReportCohortSummary(report);
  const isPending = isHubReportPendingId(String(report.id));
  const isProcessing = isPending || report.status === "processing" || dismissing;
  const strategyId =
    report.strategy_id?.trim() || report.strategy?.id?.trim() || "";
  const strategyName = report.strategy?.name?.trim() || null;
  const pnlColor =
    rangeStats == null
      ? undefined
      : rangeStats.totalPnl >= 0
        ? "var(--tessera-pnl-positive)"
        : "#E00101";

  const statsLine = isProcessing
    ? dismissing
      ? "Generation failed"
      : "Generating report…"
    : rangeStats
      ? (
          <>
            <span data-trade-log-pnl="" className="font-medium" style={{ color: pnlColor }}>
              {formatReportCardCurrency(rangeStats.totalPnl)}
            </span>
            {" · "}
            {rangeStats.totalTrades} trades ·{" "}
            {Number.isFinite(rangeStats.winRate)
              ? `${rangeStats.winRate.toFixed(1)}% win rate`
              : "— win rate"}
          </>
        )
      : (
          "Stats unavailable"
        );

  return (
    <article
      data-workspace-hub-entity-card="reports"
      data-workspace-hub-report-status={
        dismissing ? "dismissing" : isProcessing ? "processing" : report.status ?? "ready"
      }
      className={cn(
        ENTITY_CARD_CLASS,
        "transition-[opacity,transform] duration-300 ease-out",
        isProcessing && !dismissing && "border-[#38BBD1]/60",
        dismissing && "pointer-events-none -translate-y-1 opacity-0"
      )}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          data-workspace-hub-entity-title=""
          className={cn(CARD_TITLE_CLASS, "min-w-0 flex-1")}
          onClick={() => {
            if (!isPending && !dismissing) onOpen(report);
          }}
          disabled={isPending || dismissing}
        >
          {title}
        </button>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {isProcessing ? (
            <span className="inline-flex items-center gap-1 rounded border border-cyan-500/40 bg-cyan-500/10 px-1.5 py-0.5 font-[family-name:var(--font-primary)] text-xs text-cyan-700 dark:text-cyan-200">
              {dismissing ? null : (
                <Loader2 className="size-3 animate-spin text-[#38BBD1]" aria-hidden />
              )}
              {dismissing ? "Failed" : "Generating…"}
            </span>
          ) : (
            <Badge
              data-reports-type-badge=""
              data-report-type={getReportTypeBadgeDataType(report.report_type)}
              className={cn(getReportTypeBadgeClass(report.report_type), "shrink-0")}
              title={reportType}
            >
              {reportType}
            </Badge>
          )}
        </div>
      </div>

      <p className={CARD_DESCRIPTION_CLASS}>{statsLine}</p>

      <div className={ENTITY_CARD_FOOTER_CLASS}>
        <div className="min-w-0">
          <p className={META_LABEL_CLASS}>Created</p>
          <p className={CARD_UPDATED_CLASS}>
            {formatWorkspaceHubDate(report.created_at)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {!isProcessing ? (
            <div
              className="flex shrink-0 items-center gap-1.5"
              onClick={(event) => event.stopPropagation()}
            >
              <ReportsMetaIconChip
                id="window"
                label="Window"
                value={windowLabel}
                icon={Calendar}
              />
              <ReportsMetaIconChip
                id="timeframe"
                label="Timeframe"
                value={timeframe}
                icon={CandlestickChart}
              />
            </div>
          ) : null}
          {strategyId ? (
            <HubEntityLinkIconButton
              kind="strategy"
              detail={strategyName}
              onOpen={() => onOpenStrategyById(strategyId)}
            />
          ) : null}
          {!isPending && !dismissing ? (
            <HubEntityOpenButton
              label={`Open ${title}`}
              onClick={() => onOpen(report)}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}

function SimulationSummaryMetric({
  label,
  value,
  delta,
  toneDelta = true,
  hint,
}: {
  label: string;
  value: string;
  delta: number | null;
  /** Trades change stays gray in simulator summary. */
  toneDelta?: boolean;
  hint?: string;
}): React.ReactElement {
  return (
    <div className="min-w-0 flex-1 text-left">
      <p className={META_LABEL_CLASS}>{label}</p>
      <p
        className={cn(
          "truncate font-[family-name:var(--font-primary)] text-xs font-medium tabular-nums leading-snug",
          toneDelta ? cnSimulatorMetricsDelta(delta, "") : SIMULATOR_METRICS_GRAY
        )}
      >
        {value}
      </p>
      {hint ? (
        <p className="truncate font-[family-name:var(--font-primary)] text-[9px] text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function SimulationEntityCard({
  backtest,
  strategyName,
  reportTitle,
  onOpen,
  onOpenStrategyById,
  onOpenReportById,
}: {
  backtest: Backtest;
  strategyName?: string | null;
  reportTitle?: string | null;
  onOpen: (backtest: Backtest) => void;
  onOpenStrategyById: (strategyId: string) => void;
  onOpenReportById: (reportId: string) => void;
}): React.ReactElement {
  const title = backtest.title?.trim() || "Untitled simulation";
  const reportId = backtest.report_id?.trim() || "";
  const strategyId = backtest.strategy_id?.trim() || "";
  const isReport = Boolean(reportId);
  const originLabel = isReport ? "From report" : "Standalone";
  const {
    pnlDelta,
    winRateDelta,
    tradesDelta,
    tradesRemoved,
    tradesChanged,
    tradesDeltaLabel,
  } = simulationSummaryDeltas(backtest);
  const simulationMeta = buildSimulationEntityCardDetail(backtest);
  const windowLabel =
    simulationMeta.kind === "simulation" ? simulationMeta.windowLabel : "—";
  const timeframe =
    simulationMeta.kind === "simulation" ? simulationMeta.timeframe : "—";
  const hasSummary =
    pnlDelta != null ||
    winRateDelta != null ||
    tradesDelta != null ||
    tradesChanged != null ||
    tradesRemoved != null;
  const showHybridRemoved =
    tradesChanged != null &&
    tradesRemoved != null &&
    tradesRemoved !== 0 &&
    tradesRemoved !== tradesChanged;

  return (
    <article
      data-workspace-hub-entity-card="simulations"
      className={ENTITY_CARD_CLASS}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          data-workspace-hub-entity-title=""
          className={cn(CARD_TITLE_CLASS, "min-w-0 flex-1")}
          onClick={() => onOpen(backtest)}
        >
          {title}
        </button>
        <Badge
          data-workspace-hub-origin-badge={isReport ? "report" : "standalone"}
          variant="outline"
          className={cn(ORIGIN_BADGE_CLASS, "shrink-0")}
          title={originLabel}
        >
          {originLabel}
        </Badge>
      </div>

      {hasSummary ? (
        <div
          data-workspace-hub-simulation-summary=""
          className="flex min-h-8 min-w-0 items-start gap-2"
        >
          <SimulationSummaryMetric
            label="PnL Change"
            delta={pnlDelta}
            value={pnlDelta == null ? "—" : formatSignedNumber(pnlDelta, 2)}
          />
          <SimulationSummaryMetric
            label="Win rate"
            delta={winRateDelta}
            value={
              winRateDelta == null ? "—" : `${formatSignedNumber(winRateDelta)}%`
            }
          />
          <SimulationSummaryMetric
            label={tradesDeltaLabel || "Trades"}
            delta={tradesDelta}
            toneDelta={false}
            value={tradesDelta == null ? "—" : formatSignedNumber(tradesDelta, 0)}
            hint={
              showHybridRemoved
                ? `${formatSignedNumber(tradesRemoved, 0)} removed`
                : undefined
            }
          />
        </div>
      ) : (
        <p className={CARD_DESCRIPTION_CLASS}>Summary unavailable</p>
      )}

      <div className={ENTITY_CARD_FOOTER_CLASS}>
        <div className="min-w-0">
          <p className={META_LABEL_CLASS}>Created</p>
          <p className={CARD_UPDATED_CLASS}>{formatWorkspaceHubDate(backtest.created_at)}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <div
            className="flex shrink-0 items-center gap-1.5"
            onClick={(event) => event.stopPropagation()}
          >
            <ReportsMetaIconChip
              id="window"
              label="Window"
              value={windowLabel}
              icon={Calendar}
            />
            <ReportsMetaIconChip
              id="timeframe"
              label="Timeframe"
              value={timeframe}
              icon={CandlestickChart}
            />
          </div>
          {strategyId ? (
            <HubEntityLinkIconButton
              kind="strategy"
              detail={strategyName}
              onOpen={() => onOpenStrategyById(strategyId)}
            />
          ) : null}
          {reportId ? (
            <HubEntityLinkIconButton
              kind="report"
              detail={reportTitle}
              onOpen={() => onOpenReportById(reportId)}
            />
          ) : null}
          <HubEntityOpenButton
            label={`Open ${title}`}
            onClick={() => onOpen(backtest)}
          />
        </div>
      </div>
    </article>
  );
}

function laneBody(
  lane: HubEntityLaneConfig,
  props: WorkspaceHubEntitiesBoardProps
): React.ReactElement {
  if (props.loading) {
    return <HubLaneLoading label={lane.label} />;
  }
  if (lane.key === "strategies") {
    if (props.strategies.length === 0) {
      return <HubLaneEmpty message={lane.emptyLabel} />;
    }
    return (
      <>
        {props.strategies.map((strategy) => (
          <StrategyEntityCard
            key={strategy.id}
            strategy={strategy}
            onOpen={props.onOpenStrategy}
          />
        ))}
      </>
    );
  }
  if (lane.key === "reports") {
    if (props.reports.length === 0) {
      return <HubLaneEmpty message={lane.emptyLabel} />;
    }
    return (
      <>
        {props.reports.map((report) => (
          <ReportEntityCard
            key={report.id}
            report={report}
            dismissing={props.dismissingReportIds?.has(report.id) ?? false}
            onOpen={props.onOpenReport}
            onOpenStrategyById={props.onOpenStrategyById}
          />
        ))}
      </>
    );
  }
  if (props.backtests.length === 0) {
    return <HubLaneEmpty message={lane.emptyLabel} />;
  }
  return (
    <>
      {props.backtests.map((backtest) => {
        const strategyId = backtest.strategy_id?.trim() || "";
        const reportId = backtest.report_id?.trim() || "";
        const strategyName =
          props.strategies.find((s) => s.id === strategyId)?.name?.trim() || null;
        const linkedReport = props.reports.find((r) => r.id === reportId);
        const reportTitle = linkedReport
          ? getReportDisplayTitle(linkedReport)
          : null;
        return (
          <SimulationEntityCard
            key={backtest.id}
            backtest={backtest}
            strategyName={strategyName}
            reportTitle={reportTitle}
            onOpen={props.onOpenSimulation}
            onOpenStrategyById={props.onOpenStrategyById}
            onOpenReportById={props.onOpenReportById}
          />
        );
      })}
    </>
  );
}

function laneCount(lane: HubEntityLaneConfig, props: WorkspaceHubEntitiesBoardProps): number {
  if (lane.key === "strategies") return props.strategies.length;
  if (lane.key === "reports") return props.reports.length;
  return props.backtests.length;
}

/**
 * Account-scoped strategies / reports / simulations as a 3-column board
 * (tabs below `lg`). Card meta mirrors the former hub table columns.
 */
export function WorkspaceHubEntitiesBoard(
  props: WorkspaceHubEntitiesBoardProps
): React.ReactElement {
  const isBelowLg = useIsBelowLg();
  const [activeTab, setActiveTab] = React.useState<HubEntityLaneKey>("strategies");

  if (!isBelowLg) {
    return (
      <div
        data-workspace-hub-entities-board=""
        className="grid w-full min-w-0 grid-cols-1 gap-4 lg:grid-cols-3"
      >
        {HUB_ENTITY_LANES.map((lane) => (
          <HubEntityLaneShell
            key={lane.key}
            lane={lane}
            count={laneCount(lane, props)}
            countLoading={props.loading}
            onCreate={laneCreateHandler(lane, props)}
          >
            {laneBody(lane, props)}
          </HubEntityLaneShell>
        ))}
      </div>
    );
  }

  return (
    <Tabs
      data-trade-log-tabs=""
      data-workspace-hub-entities-board-tabs=""
      value={activeTab}
      onValueChange={(value) => setActiveTab(value as HubEntityLaneKey)}
      className="w-full min-w-0"
    >
      <TabsList className="w-full min-w-0">
        {HUB_ENTITY_LANES.map((lane) => (
          <TabsTrigger key={lane.key} value={lane.key} className="gap-1.5">
            <span className="truncate">{lane.label}</span>
            <Badge
              variant="outline"
              data-workspace-hub-lane-count=""
              className={cn(LANE_COUNT_BADGE_CLASS, "h-4 min-w-4 px-1 text-[10px]")}
            >
              {props.loading ? "…" : laneCount(lane, props)}
            </Badge>
          </TabsTrigger>
        ))}
      </TabsList>

      {HUB_ENTITY_LANES.map((lane) => (
        <TabsContent key={lane.key} value={lane.key} className="mt-0 outline-none">
          <HubEntityLaneShell
            lane={lane}
            count={laneCount(lane, props)}
            countLoading={props.loading}
            onCreate={laneCreateHandler(lane, props)}
          >
            {laneBody(lane, props)}
          </HubEntityLaneShell>
        </TabsContent>
      ))}
    </Tabs>
  );
}
